<template>
  <div class="interaction-files">
    <div class="interaction-files__list">
      <BasePreloader v-if="pending"/>
      <OverlayScrollbarsComponent ref="scrollBar"
                                  class="interaction-chat__list-scroll  os-host-flexbox"
                                  :options="scrollOptions">
        <div class="interaction-files__list-content">
          <template v-for="(item, index) in messages">
            <div :key="item.file.id"
                 class="interaction-files__spacer"
                 v-if="!(messages[index - 1] && sameDate(messages[index - 1].file.created_at, item.file.created_at))"
            >
              <span v-if="isToday(item.file.created_at)">{{ $tInteraction('Today') }}</span>
              <span v-else>{{ item.file.created_at | formatDate }}</span>
            </div>

            <div :key="item.file.id + 'message'"
                 :class="{'_you': item.file.customer_id === user.id}"
                 class="interaction-files__message">
              <BasePreloader v-if="item.pending"/>
              <div class="interaction-files__message-head">
                <div class="interaction-files__message-author">
                  <template v-if="item.file.customer_id === user.id">{{ $tInteraction('You') }}</template>
                  <template v-else>
                    <div class="interaction-files__message-author-name">{{
                        item.customer && item.customer.firstname
                      }}
                    </div>
                    <div v-if="!item.viewed"
                         class="interaction-files__message-status"></div>
                  </template>
                </div>
                <div class="interaction-files__message-date">
                  <span>{{ item.file.created_at | formatDate }}</span>
                  <span>{{ item.file.created_at | formatTime }}</span></div>
              </div>
              <div class="interaction-files__message-file">
                <BaseSvg name="ico-file-simple"
                         class="interaction-files__message-file-icon"/>
                {{ item.file.name }}
              </div>
              <div class="interaction-files__message-file-option">
                <div class="interaction-files__message-file-params">
                  <span style="text-transform: uppercase">{{ item.file.ext }}</span>
                  {{ item.file.size | formatSize }}
                </div>
                <button v-if="item.file.customer_id === user.id"
                        @click.prevent="onDelete(index)"
                        class="interaction-files__message-file-download  inline-link  _delete">
                  <BaseSvg name="ico-close"
                           width="0.9rem"
                           height="1.1rem"/>
                  <span>{{ $tInteraction('Delete') }}</span>
                </button>
                <a v-else
                   :href="item.file.path"
                   download
                   @click.prevent="onDownload(item.file)"
                   class="interaction-files__message-file-download  inline-link">
                  <BaseSvg name="ico-download"
                           width="0.9rem"
                           height="1.1rem"/>
                  <span>{{ $tInteraction('Download') }}</span>
                </a>
              </div>
            </div>
          </template>
        </div>
      </OverlayScrollbarsComponent>
    </div>
    <button @click.prevent="onDownloadAll"
            class="interaction-files__download-all  base-btn  base-btn--small base-btn--outline-blue">
      <BaseSvg width="0.9rem"
               height="1.1rem"
               name="ico-download"/>
      <span>{{ $tInteraction('Download all files') }}</span>
    </button>

    <InteractionFilesDropzone :pending="pendingSend"
                              @upload="onUpload"
                              @error="onError"/>

    <BaseAlert :value="error"
               class="interaction-files__error  _warning"/>
    <div class="interaction-toggle d-none d-md-flex">
      <button type="button"
              class="interaction-toggle__btn"
              @click.prevent="onClose">
        <BaseSvg name="ico-gear-2"
                 width="1.5rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Actions') }}</span>
      </button>
      <button type="button"
              @click.prevent="onOpenTab('showChatMobile')"
              class="interaction-toggle__btn  _next-active">
        <BaseSvg name="ico-msg"
                 width="1.7rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Chat') }}</span>
      </button>
      <button type="button"
              :class="hasNewFromServer ? '_accent' :  null"
              class="interaction-toggle__btn  _active">
        <BaseSvg name="ico-msg"
                 width="1.7rem"
                 height="1.5rem"/>
        <span>{{ $tInteraction('Files') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import {mapActions, mapState, mapMutations} from 'vuex'
  import parseDateForLocal from '@/helpers/parseDateForLocal'
  import download from '@/helpers/download'
  import {baseURL} from '@/axios'
  import {OverlayScrollbarsComponent} from 'overlayscrollbars-vue'
  import InteractionFilesDropzone from './InteractionFilesDropzone'

  export default {
    name: 'InteractionFiles',
    components: {
      OverlayScrollbarsComponent,
      InteractionFilesDropzone
    },
    data() {
      return {
        scrollOptions: {
          overflowBehavior: {
            x: 'hidden',
            y: 'scroll'
          },
          callbacks: {
            onScrollStop: this.checkTop
          }
        },
        messageText: '',
        timer: null,
        prevScrollY: 0
      }
    },
    computed: {
      ...mapState('user', {
        'user': 'profile'
      }),
      ...mapState('interaction/files', [
        'messages',
        'hasNewFromServer',
        'pending',
        'pendingSend',
        'error',
      ]),
      scrollBar() {
        return this.$refs.scrollBar && this.$refs.scrollBar.osInstance()
      },
    },
    methods: {
      ...mapMutations('interaction/files', [
        'setProperty',
        'resetState',
      ]),
      ...mapActions('interaction/files', [
        'load',
        'deleteMessage',
        'sendMessage',
        'loadNewMessages',
        'setViewed',
      ]),

      onDownload(file) {
        download(file.path, file.name_for_download)
      },

      onDownloadAll() {
        const id = this.$route.params.id
        const url = new URL(`/api/new-interaction/${id}/files-tab/download-all`, baseURL)
        download(url.href, `interaction-files-${id}`, 'zip', true)
      },
      onDelete(index) {
        this.deleteMessage(index)
      },

      onClose() {
        this.$emit('close')
      },
      onOpenTab(key) {
        this.$emit('open-tab', key)
      },

      async onUpload(files) {
        await this.sendMessage(
            {
              files
            }
        )

        this.checkScroll()
      },
      onError(e) {
        this.setProperty(['error', e])
      },
      checkScroll() {
        if (!this.scrollBar) return
        const currentScroll = this.scrollBar.scroll()

        if (currentScroll.position.y > currentScroll.max.y - 10) {
          setTimeout(() => {
            this.scrollBar.scroll({y: '100%'}, 200)
          }, 50)
        }
      },
      sameDate(prev, next) {
        let prevDay = parseDateForLocal(prev).getDate()
        let nextDay = parseDateForLocal(next).getDate()

        return prevDay === nextDay;
      },
      isToday(date) {
        let today = new Date().getDate()
        return today === parseDateForLocal(date).getDate()
      },
    },
    async mounted() {
      this.resetState()
      await this.load(this.$route.params.id)

      if (this.scrollBar) {
        this.scrollBar.scroll({y: '100%'}, 0)
      }
    },
    beforeDestroy() {

    },
    watch: {
      messages(to, from) {
        if (to.length && (!from.length || to[to.length - 1].file.id !== from[from.length - 1].file.id)) {
          this.checkScroll()
        }

        this.setViewed()
      },
      hasNewFromServer(has) {
        if (!has) return
        this.loadNewMessages()
      }
    }
  }
</script>

<style lang="less">

</style>